import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

const PLAN_COLORS = {
  STARTER: 'bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300',
  PRO: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  PREMIUM: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
};

const StatCard = ({ icon, label, value, sub, color, testId }) => (
  <Card data-testid={testId}>
    <CardContent className="pt-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-muted-foreground mb-1">{label}</p>
          <p className="text-2xl font-bold font-['Manrope']">{value}</p>
          {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
        </div>
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color}`}>
          {icon}
        </div>
      </div>
    </CardContent>
  </Card>
);

export const AdminDashboard = ({ stats }) => {
  return (
    <div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        <StatCard icon={<UsersIcon />} label="Utilisateurs" value={stats?.totalUsers || 0} sub={`${stats?.activeUsers || 0} actifs`} color="text-blue-500 bg-blue-500/10" testId="stat-users" />
        <StatCard icon={<StoreIcon />} label="Boutiques" value={stats?.totalShops || 0} sub={(stats?.publishedShops || 0) + " publi\u00e9es"} color="text-emerald-500 bg-emerald-500/10" testId="stat-shops" />
        <StatCard icon={<PackageIcon />} label="Produits" value={stats?.totalProducts || 0} sub={(stats?.totalCategories || 0) + " cat\u00e9gories"} color="text-violet-500 bg-violet-500/10" testId="stat-products" />
        <StatCard icon={<DollarIcon />} label="Revenu mensuel" value={(stats?.monthlyRevenue || 0) + "\u20ac"} sub={"estim\u00e9"} color="text-amber-500 bg-amber-500/10" testId="stat-revenue" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle className="text-base">{"R\u00e9partition des plans"}</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-4" data-testid="plan-distribution">
              {['STARTER', 'PRO', 'PREMIUM'].map(plan => {
                const count = stats?.usersByPlan?.[plan] || 0;
                const total = stats?.totalUsers || 1;
                const pct = Math.round((count / total) * 100);
                return (
                  <div key={plan}>
                    <div className="flex justify-between items-center mb-1.5">
                      <div className="flex items-center gap-2">
                        <Badge className={PLAN_COLORS[plan]}>{plan}</Badge>
                        <span className="text-sm font-medium">{count} utilisateur{count !== 1 ? 's' : ''}</span>
                      </div>
                      <span className="text-sm text-muted-foreground">{pct}%</span>
                    </div>
                    <div className="w-full h-2 rounded-full bg-muted overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${plan === 'STARTER' ? 'bg-zinc-400' : plan === 'PRO' ? 'bg-blue-500' : 'bg-amber-500'}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">{"Inscriptions r\u00e9centes"}</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-3" data-testid="registrations-chart">
              {(stats?.registrationsOverTime || []).slice(-10).map((r, i) => {
                const maxCount = Math.max(...(stats?.registrationsOverTime || []).map(x => x.count), 1);
                return (
                  <div key={i} className="flex items-center gap-3">
                    <span className="text-xs text-muted-foreground w-20 shrink-0">{new Date(r.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}</span>
                    <div className="flex-1 h-5 rounded bg-muted overflow-hidden">
                      <div className="h-full rounded bg-primary transition-all duration-500" style={{ width: `${(r.count / maxCount) * 100}%` }} />
                    </div>
                    <span className="text-sm font-semibold w-6 text-right">{r.count}</span>
                  </div>
                );
              })}
              {(!stats?.registrationsOverTime || stats.registrationsOverTime.length === 0) && (
                <p className="text-sm text-muted-foreground text-center py-4">Aucune donn&eacute;e</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const UsersIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>;
const StoreIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m2 7 4.41-4.41A2 2 0 0 1 7.83 2h8.34a2 2 0 0 1 1.42.59L22 7"/><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><path d="M15 22v-4a2 2 0 0 0-2-2h-2a2 2 0 0 0-2 2v4"/><path d="M2 7h20"/><path d="M22 7v3a2 2 0 0 1-2 2a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 16 12a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 12 12a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 8 12a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 4 12a2 2 0 0 1-2-2V7"/></svg>;
const PackageIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m7.5 4.27 9 5.15"/><path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"/><path d="m3.3 7 8.7 5 8.7-5"/><path d="M12 22V12"/></svg>;
const DollarIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" x2="12" y1="2" y2="22"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>;

export { PLAN_COLORS };
export default AdminDashboard;
